import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, authFetch } from '../../context/AuthContext';
import { AlertTriangle, ArrowDownToLine, ArrowRight, ArrowUpFromLine, BarChart3, Boxes, Building2, Clock3, Factory, Inbox, Plus, RefreshCw, ShoppingBag, Tag } from 'lucide-react';

/* ── 타입 ── */
interface StockItem   { id: number; name: string; stock: number; is_low: number; category: string; }
interface ProdOrder   { id: number; product_name: string; category: string | null; quantity: number; due_date: string; status: string; partner_name: string; }
interface Receipt     { id: number; supplier_name: string; receipt_date: string; total_amount: number; }
interface Shipment    { id: number; client_name: string; shipment_date: string; total_amount: number; }

/* ── 상수 ── */
const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending:    { label: '접수',        color: 'bg-stone-100 text-stone-600' },
  preparing:  { label: '원자재 준비', color: 'bg-blue-100 text-blue-700' },
  producing:  { label: '생산 중',     color: 'bg-amber-100 text-amber-700' },
  inspection: { label: '검수',        color: 'bg-purple-100 text-purple-700' },
  completed:  { label: '완료',        color: 'bg-green-100 text-green-700' },
  delayed:    { label: '지연',        color: 'bg-red-100 text-red-700' },
};

const QUICK_MENU = [
  { to: '/admin/inventory',            label: '재고 현황',       icon: Boxes, desc: '전체 재고 조회' },
  { to: '/admin/inventory?action=in',  label: '자재 입고',       icon: ArrowDownToLine, desc: '거래명세표 입고 처리' },
  { to: '/admin/inventory?action=out', label: '상품 출고',       icon: ArrowUpFromLine, desc: '거래명세표 출고 처리' },
  { to: '/admin/production',           label: '생산 현황',       icon: Factory, desc: '생산 단계별 현황' },
  { to: '/admin/production?action=new',label: '생산 주문 등록',  icon: Plus, desc: '신규 생산 주문 접수' },
  { to: '/admin/catalog',              label: '제품 카탈로그',   icon: ShoppingBag, desc: '제품 등록·상세 편집' },
  { to: '/admin/inquiries',            label: '제작 문의',       icon: Inbox, desc: 'OEM/ODM 문의 관리' },
  { to: '/admin/vendors',              label: '거래처 관리',     icon: Building2, desc: '공급업체 관리' },
  { to: '/admin/report',               label: '리포트',          icon: BarChart3, desc: '입출고·가입자 통계' },
];

/* ── 오늘 / 이번달 ── */
const today      = new Date().toISOString().split('T')[0];
const monthStart = today.slice(0, 7) + '-01';
const fmt        = (n: number) => n.toLocaleString() + '원';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  /* 상태 */
  const [inventory,   setInventory]   = useState<StockItem[]>([]);
  const [productions, setProductions] = useState<ProdOrder[]>([]);
  const [receipts,    setReceipts]    = useState<Receipt[]>([]);
  const [shipments,   setShipments]   = useState<Shipment[]>([]);
  const [waitingInq,  setWaitingInq]  = useState(0);
  const [loading,     setLoading]     = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const qs = `from=${monthStart}&to=${today}`;
    const [inv, prod, rec, ship, inqSum] = await Promise.all([
      authFetch('/api/inventory').then(r => r.ok ? r.json() : []),
      authFetch('/api/production').then(r => r.ok ? r.json() : []),
      authFetch(`/api/receipts?${qs}`).then(r => r.ok ? r.json() : []),
      authFetch(`/api/shipments?${qs}`).then(r => r.ok ? r.json() : []),
      authFetch('/api/oem/summary').then(r => r.ok ? r.json() : { waiting: 0 }),
    ]);
    setInventory(inv);
    setProductions(prod);
    setReceipts(rec);
    setShipments(ship);
    setWaitingInq((inqSum as any).waiting ?? 0);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  /* 통계 계산 */
  const totalProducts   = inventory.length;
  const totalStock      = inventory.reduce((s, p) => s + p.stock, 0);
  const lowStockCount   = inventory.filter(p => p.is_low === 1).length;
  const activeProds     = productions.filter(p => !['completed','delayed'].includes(p.status)).length;
  const delayedProds    = productions.filter(p => p.status === 'delayed').length;
  const monthlyInAmt    = receipts.reduce((s, r) => s + r.total_amount, 0);
  const monthlyOutAmt   = shipments.reduce((s, r) => s + r.total_amount, 0);
  const soldOutCount    = inventory.filter(p => p.category === '내부판매용' && p.stock === 0).length;

  const statCards = [
    { label: '전체 제품',   value: totalProducts,              sub: '종',     color: 'bg-white text-stone-800',     link: '/admin/inventory' },
    { label: '전체 재고',   value: totalStock.toLocaleString(), sub: '개',    color: 'bg-white text-stone-800',     link: '/admin/inventory' },
    { label: '재고 부족',   value: lowStockCount,              sub: '개 품목', color: 'bg-red-50 text-red-800',     link: '/admin/inventory?filter=low' },
    { label: '진행 중 생산', value: activeProds,               sub: '건',     color: 'bg-amber-50 text-amber-800', link: '/admin/production' },
    { label: '이달 입고',   value: fmt(monthlyInAmt),          sub: `${receipts.length}건`, color: 'bg-green-50 text-green-800', link: '/admin/report' },
    { label: '이달 출고',   value: fmt(monthlyOutAmt),         sub: `${shipments.length}건`, color: 'bg-blue-50 text-blue-800',   link: '/admin/report' },
  ];

  /* 최근 생산 — 진행 중·지연 우선 */
  const recentProds = [...productions]
    .sort((a, b) => {
      const priority: Record<string, number> = { delayed: 0, producing: 1, preparing: 2, pending: 3, inspection: 4, completed: 5 };
      return (priority[a.status] ?? 9) - (priority[b.status] ?? 9);
    })
    .slice(0, 6);

  /* 최근 입고·출고 */
  const recentReceipts  = receipts.slice(0, 4);
  const recentShipments = shipments.slice(0, 4);

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* 헤더 */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">대시보드</h1>
          <p className="text-stone-500 text-sm mt-1">
            안녕하세요, <strong>{user?.name}</strong>님 ({user?.partner}) &nbsp;·&nbsp;
            <span className="text-stone-400">{today}</span>
          </p>
        </div>
        <button onClick={load} className="text-xs text-stone-400 hover:text-amber-600 transition flex items-center gap-1">
          <RefreshCw size={14} /> 새로고침
        </button>
      </div>

      {/* 알림 배너 */}
      {!loading && (lowStockCount > 0 || delayedProds > 0 || soldOutCount > 0 || waitingInq > 0) && (
        <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {lowStockCount > 0 && (
            <button onClick={() => navigate('/admin/inventory?filter=low')}
              className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-left hover:bg-red-100 transition flex items-center gap-3">
              <AlertTriangle size={20} aria-hidden="true" />
              <div>
                <p className="text-sm font-bold text-red-700">재고 부족 {lowStockCount}개 품목</p>
                <p className="text-xs text-red-500">즉시 입고 처리가 필요합니다</p>
              </div>
            </button>
          )}
          {delayedProds > 0 && (
            <button onClick={() => navigate('/admin/production')}
              className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-left hover:bg-orange-100 transition flex items-center gap-3">
              <Clock3 size={20} aria-hidden="true" />
              <div>
                <p className="text-sm font-bold text-orange-700">생산 지연 {delayedProds}건</p>
                <p className="text-xs text-orange-500">지연 해제 처리가 필요합니다</p>
              </div>
            </button>
          )}
          {soldOutCount > 0 && (
            <button onClick={() => navigate('/admin/catalog')}
              className="bg-stone-100 border border-stone-200 rounded-xl px-4 py-3 text-left hover:bg-stone-200 transition flex items-center gap-3">
              <Tag size={20} aria-hidden="true" />
              <div>
                <p className="text-sm font-bold text-stone-700">SOLD OUT {soldOutCount}개</p>
                <p className="text-xs text-stone-500">내부판매용 재고 소진</p>
              </div>
            </button>
          )}
          {waitingInq > 0 && (
            <button onClick={() => navigate('/admin/inquiries')}
              className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-left hover:bg-amber-100 transition flex items-center gap-3">
              <Inbox size={20} aria-hidden="true" />
              <div>
                <p className="text-sm font-bold text-amber-700">미처리 문의 {waitingInq}건</p>
                <p className="text-xs text-amber-600">OEM/ODM 대기 문의</p>
              </div>
            </button>
          )}
        </div>
      )}

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map(c => (
          <button key={c.label} onClick={() => navigate(c.link)}
            className={`rounded-xl p-4 ${c.color} shadow-sm text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 group`}>
            <p className="text-xs font-medium opacity-60 mb-1">{c.label}</p>
            <p className={`font-bold ${String(c.value).length > 7 ? 'text-lg' : 'text-2xl'}`}>{loading ? '—' : c.value}</p>
            <p className="text-xs mt-1 opacity-50">{c.sub}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">

        {/* 최근 생산 현황 */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-bold text-stone-900">생산 현황</h2>
            <Link to="/admin/production" className="text-amber-600 text-sm hover:underline">전체 보기</Link>
          </div>
          {loading ? (
            <div className="text-center py-8 text-stone-400 text-sm animate-pulse">불러오는 중...</div>
          ) : recentProds.length === 0 ? (
            <div className="text-center py-8 text-stone-400 text-sm">등록된 생산 주문이 없습니다.</div>
          ) : (
            <div className="space-y-1">
              {recentProds.map(p => (
                <Link key={p.id} to="/admin/production"
                  className="flex items-center justify-between py-2.5 px-2 rounded-lg border-b border-stone-50 last:border-0 hover:bg-stone-50 transition">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-stone-800 text-sm truncate">{p.product_name}</p>
                      {p.category && (
                        <span className="text-xs bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded flex-shrink-0">{p.category}</span>
                      )}
                    </div>
                    <p className="text-xs text-stone-400">{p.quantity}개 · 납기 {p.due_date} · {p.partner_name}</p>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ml-2 flex-shrink-0 ${(STATUS_LABELS[p.status] ?? STATUS_LABELS.pending).color}`}>
                    {(STATUS_LABELS[p.status] ?? { label: p.status }).label}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* 빠른 메뉴 */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="font-bold text-stone-900 mb-3">빠른 메뉴</h2>
          <div className="space-y-1">
            {QUICK_MENU.map(item => {
              const Icon = item.icon;
              return (
              <Link key={item.to} to={item.to}
                className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-amber-50 border border-transparent hover:border-amber-200 transition group">
                <Icon size={18} className="w-7 flex-shrink-0 text-stone-600" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-stone-700 group-hover:text-amber-700">{item.label}</p>
                  <p className="text-xs text-stone-400">{item.desc}</p>
                </div>
                <ArrowRight size={14} className="text-stone-300 group-hover:text-amber-500 flex-shrink-0" />
              </Link>
            )})}
          </div>
        </div>
      </div>

      {/* 이달 입출고 현황 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* 이달 자재 입고 */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-bold text-stone-900 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block"/>
              이달 자재 입고
            </h2>
            <Link to="/admin/report" className="text-amber-600 text-sm hover:underline">리포트</Link>
          </div>
          {loading ? (
            <div className="text-center py-6 text-stone-400 text-sm animate-pulse">불러오는 중...</div>
          ) : recentReceipts.length === 0 ? (
            <div className="text-center py-6 text-stone-400 text-sm">이달 입고 내역이 없습니다.</div>
          ) : (
            <>
              <div className="space-y-2 mb-3">
                {recentReceipts.map(r => (
                  <div key={r.id} className="flex items-center justify-between text-sm py-1.5 border-b border-stone-50 last:border-0">
                    <div>
                      <p className="font-medium text-stone-700">{r.supplier_name}</p>
                      <p className="text-xs text-stone-400">{r.receipt_date}</p>
                    </div>
                    <span className="font-semibold text-green-600 text-sm">{r.total_amount.toLocaleString()}원</span>
                  </div>
                ))}
              </div>
              <div className="bg-green-50 rounded-xl px-4 py-2.5 flex justify-between items-center">
                <span className="text-xs text-green-700 font-semibold">이달 합계 ({receipts.length}건)</span>
                <span className="text-green-700 font-bold">{monthlyInAmt.toLocaleString()}원</span>
              </div>
            </>
          )}
        </div>

        {/* 이달 상품 출고 */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-bold text-stone-900 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"/>
              이달 상품 출고
            </h2>
            <Link to="/admin/report" className="text-amber-600 text-sm hover:underline">리포트</Link>
          </div>
          {loading ? (
            <div className="text-center py-6 text-stone-400 text-sm animate-pulse">불러오는 중...</div>
          ) : recentShipments.length === 0 ? (
            <div className="text-center py-6 text-stone-400 text-sm">이달 출고 내역이 없습니다.</div>
          ) : (
            <>
              <div className="space-y-2 mb-3">
                {recentShipments.map(s => (
                  <div key={s.id} className="flex items-center justify-between text-sm py-1.5 border-b border-stone-50 last:border-0">
                    <div>
                      <p className="font-medium text-stone-700">{s.client_name}</p>
                      <p className="text-xs text-stone-400">{s.shipment_date}</p>
                    </div>
                    <span className="font-semibold text-blue-600 text-sm">{s.total_amount.toLocaleString()}원</span>
                  </div>
                ))}
              </div>
              <div className="bg-blue-50 rounded-xl px-4 py-2.5 flex justify-between items-center">
                <span className="text-xs text-blue-700 font-semibold">이달 합계 ({shipments.length}건)</span>
                <span className="text-blue-700 font-bold">{monthlyOutAmt.toLocaleString()}원</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
